import createSelector from 'lib/createSelector'
import { arrayToObject, last, sum } from 'lib/arrayUtils'
import { getBaggageCost, getDepartingJourneys, isJourneyV2, isJourneyV2Items } from 'lib/flights'
import config from 'constants/config'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { FlightsClassTypes, FlightsFareTypes } from 'constants/flight'
import { FlightSearchParams } from 'hooks/useSearchFlights'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getFlightCalendarKey } from 'actions/FlightActions'
import { getPromotionDefaultPeriod } from 'lib/bedbankUtils'
import { isFlightItem } from 'checkout/lib/utils/flights/cart'

export const getJourneyId = createSelector(
  (state: App.State) => state.cart.flight.journey.id,
  (id: string) => id,
)

export const getDepatureAirport = createSelector(
  (state: App.State) => state.cart.flight.journey.departing.flights[0],
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.geo.airports,
  (flight, search, airports) => {
    const urlOriginAirportCode = new URLSearchParams(search).get('originAirportCode')
    const originAirportCode = flight ? flight.departingAirport : urlOriginAirportCode
    return airports.find(({ code }) => code === originAirportCode)
  },
)

export const getDestinationAirport = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => last(state.cart.flight.journey.departing.flights),
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.router.location.search,
  (offer, departingFlight, airports, search) => {
    const urlDestinationAirportCode = new URLSearchParams(search).get('destinationAirportCode')
    const destinationAirportCode = departingFlight ? departingFlight.arrivalAirport :
        (urlDestinationAirportCode || offer?.flightDestinationPort)
    return airports.find(({ code }) => code === destinationAirportCode)
  },
)

const getPassengerBaggageForJourneyKey = journeyKey => passenger => (
  passenger.addedExtras.baggage ?
    passenger.addedExtras.baggage[journeyKey] :
    undefined
)

const getBaggageSummaryForJourneyKey = (journey, passengers: Array<any>) => {
  const baggageSelections = passengers.flat().map(getPassengerBaggageForJourneyKey(journey.journeyKey))
  const ids = baggageSelections
    .filter(sel => sel && sel.count > 0)
    .map(sel => sel.id)
  const uniqueIds = [...new Set(ids)]

  return {
    counts: uniqueIds.reduce((acc: Array<any>, id: string) => {
      acc.push({ id, count: sum(baggageSelections, sel => sel.count) })
      return acc
    }, []),
    cost: getBaggageCost(passengers, journey),
  }
}

export const getAddedBaggage = createSelector(
  (state: App.State) => state.cart.flight.journey.departing,
  (state: App.State) => state.cart.flight.journey.returning,
  (state: App.State) => state.cart.flight.passengers,
  (departingJourney, returningJourney, passengers) => ({
    departing: getBaggageSummaryForJourneyKey(departingJourney, passengers),
    returning: getBaggageSummaryForJourneyKey(returningJourney, passengers),
  }),
)

export const flightsHasFiltersApplied = createSelector(
  (state: App.State) => state.flights.filters,
  (filters) => filters.some(f => f.options.some(o => o.active && !o.isDefault)),
)

export const flightsHasPriceDrop = createSelector(
  (state: App.State) => getDepartingJourneys(state),
  (state: App.State) => state.cart.flight.basePriceSnapshot,
  (journeys, estimatePrice) => {
    if (estimatePrice === 0) {
      return false
    }
    return journeys.some(j => !j.hidden && j.cost < estimatePrice)
  },
)

export const flightsHasPriceIncrease = createSelector(
  (state: App.State) => getDepartingJourneys(state),
  (state: App.State) => state.cart.flight.basePriceSnapshot,
  (journeys, estimatePrice) => {
    if (estimatePrice === 0) {
      return false
    }

    return !isJourneyV2Items(journeys) ? journeys.every(j => !j.hidden && j.cost > estimatePrice) : false // TODO: update for flights v2
  },
)

const getAirportsByCode = createSelector(
  (state: App.State) => state.geo.originAirports,
  airports => arrayToObject(airports, airport => airport.code),
)

export const getDefaultAirport = createSelector(
  (state: App.State) => state.geo.closestAirport?.code,
  (state: App.State) => state.auth.account.recentlyUsedAirportCode,
  (state: App.State) => getAirportsByCode(state),
  (closestAirportCode, recentlyUsedAirportCode, originAirports) => originAirports[recentlyUsedAirportCode] ?? originAirports[closestAirportCode],
)

export const getDefaultAirportCode = createSelector(
  (state: App.State) => getDefaultAirport(state)?.code,
  (code) => code,
)

export const isBedbankFlightBookingEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionHasFlights,
  (currentRegionHasFlights) => !!(currentRegionHasFlights && config.BEDBANK_FLIGHT_BOOKING_ENABLED),
)

export const isStandaloneFlightsEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentRegionHasFlights,
  (regionCode, regionHasFlights) => regionHasFlights && config.ENABLE_STANDALONE_FLIGHTS && (!config.STANDALONE_FLIGHT_REGIONS || config.STANDALONE_FLIGHT_REGIONS.split(',').includes(regionCode)),
)

export const getProvider = createSelector(
  (state: App.State) => state.cart.flight.journey.provider,
  (provider) => provider,
)

export const getDepartureFlight = createSelector(
  (state: App.State) => state.cart.flight.journey.departing.flights[0],
  (flight) => flight,
)

const passportRequiredCarriers = ['I5', 'TR', 'SL', 'IW', '5J', 'JT', 'IX', 'MM', 'Y4', 'ID', 'OD']

export const isPassportRequired = createSelector(
  (state: App.State) => getProvider(state),
  (state: App.State) => last(state.cart.flight.journey.departing.flights).arrivalAirport,
  (state: App.State) => getDepartureFlight(state),
  (state: App.State) => state.cart.flight.journey.validatingCarrier,
  (state: App.State) => state.cart.flight.journey,
  (provider, destination, departingFlight, validatingCarrier, journey) => {
    // Destination country requires passport
    if (provider === 'jetstar' && destination === 'HNL' && departingFlight.departingCountry === 'Australia') {
      return true
    }

    // airline required passport
    if (isJourneyV2(journey)) {
      return [
        ...journey.departing.flights.map(flight => flight.carrier),
        ...journey.returning.flights.map(flight => flight.carrier),
      ].some(carrier => passportRequiredCarriers.includes(carrier))
    }

    return passportRequiredCarriers.includes(validatingCarrier)
  },
)

export const isFlightsCredit = createSelector(
  (state: App.State) => state.router.location.search,
  (search) => {
    const { flightsCredit } = parseSearchString(search)
    return flightsCredit === 'true' && config.ENABLE_FLIGHTS_CREDIT
  },
)

export const isTourFlightsEnabled = createSelector(
  (state: App.State) => state.cart.accommodation.flightsIncluded,
  (flightsIncluded) => flightsIncluded && config.ENABLE_FLIGHTS_WITH_TOURS,
)

export const getFlightSearchValues = createSelector(
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.geo.airports,
  (search, airports) => {
    const {
      departDate,
      returnDate,
      originAirportCode,
      originAirportName,
      destinationAirportCode,
      destinationAirportName,
      maxArrivalTime,
      minReturningDepartureTime,
      source,
      fareClass,
      adults,
      children,
      infants,
      fareType,
      childrenAge,
      carrier,
    } = parseSearchString(search)

    const occupants: App.Occupants = {
      adults: parseInt(adults),
      children: parseInt(children),
      infants: parseInt(infants),
      childrenAge: childrenAge ? childrenAge.split(',').map((age) => parseInt(age)) : [],
    }

    return {
      originAirport: airports.find(a => a.code === originAirportCode),
      destinationAirport: airports.find(a => a.code === destinationAirportCode),
      departDate,
      returnDate,
      maxArrivalTime,
      source,
      minReturningDepartureTime,
      fareClass: fareClass as FlightsClassTypes,
      occupants,
      fareType: fareType as FlightsFareTypes,
      departureLocation: {
        airportCode: originAirportCode,
        airportName: originAirportName,
      },
      arrivalLocation: {
        airportCode: destinationAirportCode,
        airportName: destinationAirportName,
      },
      carrier,
    }
  },
)

/** For use with the `useSearchFlights` hook */
export const getFlightSearchParams = createSelector(
  getFlightSearchValues,
  isFlightsCredit,
  (values, flightsCredit): FlightSearchParams => ({
    startDate: values.departDate,
    endDate: values.fareType === FlightsFareTypes.RETURN ? values.returnDate : undefined,
    origin: values.originAirport?.code,
    destination: values.destinationAirport?.code,
    occupancies: [values.occupants],
    fareClass: values.fareClass,
    fareType: values.fareType,
    maxArrivalTime: values.maxArrivalTime,
    minReturningDepartureTime: values.minReturningDepartureTime,
    source: values.source,
    viewType: values.fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' : undefined,
    carrier: values.carrier,
    flightsCredit,
  }),
)

export const isFlightBedbankCalendarFetching = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (_state: OfferPageState, appState: App.State) => appState.flights.flightCalendarLoading,
  (state: OfferPageState) => state.airport,
  (state: OfferPageState) => state.duration,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentCurrency,
  (offer, fetching, airport, duration, region, currency) => !!fetching[getFlightCalendarKey({
    ...getPromotionDefaultPeriod(),
    origin: airport?.code,
    destination: offer?.flights?.airportCode,
    nights: duration,
    currency,
    region,
  })],
)

export const getFlightCalendar = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (_state: OfferPageState, appState: App.State) => appState.flights.flightCalendar,
  (state: OfferPageState) => state.airport,
  (state: OfferPageState) => state.duration,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentCurrency,
  (offer, flightCalendars, airport, duration, region, currency) => {
    return flightCalendars[getFlightCalendarKey({
      ...getPromotionDefaultPeriod(),
      origin: airport?.code,
      destination: offer?.flights?.airportCode,
      nights: duration,
      currency,
      region,
    })]
  },
)

export const isForceBundleOffer = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.router.location.search,
  (offer, search) => {
    const { forceBundleId } = parseSearchString(search)
    return !!offer?.forceBundleId || !!forceBundleId
  },
)

export const isStandaloneFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.every(isFlightItem),
)
